import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef } from 'react';

import { cn } from '@/lib/cn';

const Table = () => {};

type TableProps = ComponentPropsWithRef<'div'>;
const Root = (props: TableProps) => {
  const { className, children, ...rest } = props;
  return (
    <div
      className={cn('flex flex-col w-full flex-1 shrink-0', className)}
      {...rest}
    >
      {children}
    </div>
  );
};

const headerCva = cva(
  'flex items-center justify-between h-9 border-y shrink-0 bg-muted',
  {
    variants: {
      size: {
        sm: 'h-8',
        md: 'h-9',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);
type TableHeaderProps = VariantProps<typeof headerCva> &
  ComponentPropsWithRef<'div'>;
const Header = (props: TableHeaderProps) => {
  const { className, children, size, ...rest } = props;
  return (
    <div className={cn(headerCva({ size }), className)} {...rest}>
      {children}
    </div>
  );
};

const colCva = cva('flex shrink-0 text-xs font-medium', {
  variants: {
    size: {
      sm: 'px-3 py-2',
      md: 'px-4 py-3.5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});
type TableColProps = VariantProps<typeof colCva> & ComponentPropsWithRef<'div'>;
const Col = (props: TableColProps) => {
  const { className, children, size, ...rest } = props;
  return (
    <div className={cn(colCva({ size }), className)} {...rest}>
      {children}
    </div>
  );
};

const Row = (props: ComponentPropsWithRef<'div'>) => {
  const { className, children, ...rest } = props;
  return (
    <div
        className={cn(
        'flex justify-between transition-all duration-200 border-b shrink-0 last:border-b-0 hover:bg-muted/50',
        props.onClick && 'cursor-pointer',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

const tableCellCva = cva('flex items-center text-foreground', {
  variants: {
    size: {
      sm: 'text-xs font-medium px-3 py-2.5',
      md: 'text-sm font-medium p-4',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});
type TableCellProps = VariantProps<typeof tableCellCva> &
  ComponentPropsWithRef<'div'>;
const Cell = (props: TableCellProps) => {
  const { className, children, size, ...rest } = props;
  return (
    <div className={cn(tableCellCva({ size }), className)} {...rest}>
      {children}
    </div>
  );
};

Table.Root = Root;
Table.Header = Header;
Table.Col = Col;
Table.Row = Row;
Table.Cell = Cell;

export { Table };
